import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable, EMPTY, forkJoin } from 'rxjs';
import { map, take, catchError } from 'rxjs/operators';
import { GET, POST } from 'src/app/network/network.service';
import { environment } from 'src/environments/environment';
import { KbStoreService } from './kb-store.service';
import { KbToolResult } from '../interfaces/kb-tool-result';

export interface SessionSummary {
  title: string;
  summary: string;
  tags: string[];
  priority: 'low' | 'medium' | 'high';
  language: 'ar' | 'en' | 'mixed';
  durationSeconds: number;
  turnCount: number;
}

export type SessionState = 'idle' | 'connecting' | 'active' | 'ending' | 'summary';

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
}

@Injectable({ providedIn: 'root' })
export class GeminiLiveService {

  constructor(private kbStore: KbStoreService) {
    (window as any).dumpGeminiFrame = () => {
      this.debugDumpNextFrame = true;
      console.log('[GeminiLive] dump requested — next captured frame will open as overlay (within ~2s).');
    };
  }

  private ws: WebSocket | null = null;
  private debugDumpNextFrame = false;
  private debugOverlayVisible = false;
  private audioContext: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private screenVideo: HTMLVideoElement | null = null;
  private screenCanvas: HTMLCanvasElement | null = null;
  private screenInterval: any = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private nextAudioTime = 0;
  private transcript: TranscriptEntry[] = [];
  private currentAssistantText = '';
  private currentUserText = '';
  private sessionStartTime: number | null = null;
  private timerInterval: any = null;

  // Perceptual-hash state for frame-diff skipping (Plan 03).
  // DIFF_THRESHOLD is Hamming distance on a 256-bit signature — below this, the
  // frame is considered visually identical to the last-sent one and skipped.
  private lastFrameHash: Uint8Array | null = null;
  private readonly DIFF_THRESHOLD = 3;
  private hashCanvas: HTMLCanvasElement | null = null;

  // True while the model is streaming back its audio reply. Frame capture
  // pauses during this window — the user is listening, not interacting, so
  // frames captured here would re-bill in the sliding window for no value
  // (Plan 05).
  private isModelSpeaking = false;

  // Auto-disable screen share after this many ms of continuous sharing
  // (Plan 07). Hard cutoff — no warning. Protects against users forgetting
  // to stop sharing; they can re-enable with one click if still needed.
  private readonly VIDEO_AUTO_DISABLE_MS = 120000;
  private videoTimeout: any = null;

  // Aggressive context-window compression (Plan 06). When the live session
  // context reaches CONTEXT_TRIGGER_TOKENS, the server compresses history
  // down to CONTEXT_TARGET_TOKENS, dropping the oldest USER-turn content.
  // Defaults are much larger, so frames sit in context for ~6 turns and
  // re-bill at that multiplier. These values force eviction roughly every
  // 2 minutes, cutting the re-billing multiplier toward 2-3x. System
  // instructions are preserved across compression.
  private readonly CONTEXT_TRIGGER_TOKENS = 20000;
  // Raised from 4000 for BK-1381. Procedure bodies now enter the conversation
  // as tool results — split-grade alone is ~2,700 tokens, 68% of the old
  // target — so compression evicted the steps almost immediately and the model
  // re-fetched the same procedure, re-billing the body each time. 12000 leaves
  // room for a large procedure to survive a compression pass. The trigger is
  // unchanged, so long sessions are still bounded.
  private readonly CONTEXT_TARGET_TOKENS = 12000;

  private _sessionState = new BehaviorSubject<SessionState>('idle');
  sessionState$ = this._sessionState.asObservable();

  private _transcript = new Subject<TranscriptEntry[]>();
  transcript$ = this._transcript.asObservable();

  private _summary = new BehaviorSubject<SessionSummary | null>(null);
  summary$ = this._summary.asObservable();

  private _error = new Subject<string>();
  error$ = this._error.asObservable();

  private _elapsed = new BehaviorSubject<string>('00:00');
  elapsed$ = this._elapsed.asObservable();

  private _videoEnabled = new BehaviorSubject<boolean>(false);
  videoEnabled$ = this._videoEnabled.asObservable();

  private _videoSecondsLeft = new BehaviorSubject<number>(0);
  videoSecondsLeft$ = this._videoSecondsLeft.asObservable();
  private videoCountdownInterval: any = null;

  private _micMuted = new BehaviorSubject<boolean>(false);
  micMuted$ = this._micMuted.asObservable();

  private _sendingSnapshot = new BehaviorSubject<boolean>(false);
  sendingSnapshot$ = this._sendingSnapshot.asObservable();

  private _snapshotSent = new BehaviorSubject<boolean>(false);
  snapshotSent$ = this._snapshotSent.asObservable();
  private snapshotSentTimer: any = null;

  startSession(): void {
    if (this._sessionState.value !== 'idle') return;

    this._sessionState.next('connecting');
    this.transcript = [];
    this.currentAssistantText = '';
    this.currentUserText = '';
    this._summary.next(null);

    forkJoin({ token: this.fetchAccessToken(), kbRouter: this.kbStore.getRouter() }).pipe(
      take(1),
      catchError((err) => {
        // The router has no bundled fallback by design (BK-1381) — starting a
        // session without it means answering from the model's own guesses.
        console.error('[GeminiLive] session start failed:', err);
        this._error.next('Support assistant is unavailable right now. Please try again in a moment.');
        this._sessionState.next('idle');
        return EMPTY;
      })
    ).subscribe(({ token, kbRouter }) => {
      // Session begins audio-only. Screen share is opt-in per Plan 04 — the
      // user can enable it mid-session via enableVideo() when the assistant
      // actually needs to see the screen. Most Q&A turns don't need video,
      // and video is the single largest cost line ($3/M image tokens, ~6x
      // re-billed via sliding window).
      navigator.mediaDevices.getUserMedia({ audio: true }).then((micStream) => {
        this.micStream = micStream;
        this.openWebSocket(token, kbRouter);
      }).catch((err: any) => {
        this._error.next(err?.message || 'Microphone permission denied');
        this.reset();
      });
    });
  }

  async enableVideo(): Promise<void> {
    if (this._sessionState.value !== 'active') return;
    if (this._videoEnabled.value) return;

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        preferCurrentTab: true,
        video: { frameRate: 0.5, displaySurface: 'browser' } as MediaTrackConstraints,
        selfBrowserSurface: 'include'
      } as DisplayMediaStreamOptions);

      const surface = (screenStream.getVideoTracks()[0]?.getSettings() as any)?.displaySurface;
      if (surface !== 'browser') {
        screenStream.getTracks().forEach(t => t.stop());
        this._error.next('Please share a browser tab only, not your entire screen or window. This is required to protect sensitive data.');
        return;
      }

      this.screenStream = screenStream;
      this.startScreenCapture();
      this._videoEnabled.next(true);
      this.startVideoCountdown();
    } catch (err: any) {
      this._error.next(err?.message || 'Screen share permission denied');
    }
  }

  extendVideo(): void {
    if (!this._videoEnabled.value) return;
    this.startVideoCountdown();
  }

  async sendSnapshot(): Promise<void> {
    if (this._sessionState.value !== 'active') return;
    if (this._sendingSnapshot.value) return;
    if (this._videoEnabled.value) return;

    this._sendingSnapshot.next(true);
    let stream: MediaStream | null = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        preferCurrentTab: true,
        video: { displaySurface: 'browser' } as MediaTrackConstraints,
        selfBrowserSurface: 'include'
      } as DisplayMediaStreamOptions);

      const surface = (stream.getVideoTracks()[0]?.getSettings() as any)?.displaySurface;
      if (surface !== 'browser') {
        this._error.next('Please share a browser tab only, not your entire screen or window. This is required to protect sensitive data.');
        return;
      }

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise<void>(r => requestAnimationFrame(() => r()));

      const canvas = document.createElement('canvas');
      canvas.width = 768;
      canvas.height = 432;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, 768, 432);
      this.applyPrivacyRedactions(ctx, 768, 432);

      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      const base64 = dataUrl.split(',')[1];
      this.sendScreenFrame(base64);

      if (this.snapshotSentTimer) clearTimeout(this.snapshotSentTimer);
      this._snapshotSent.next(true);
      this.snapshotSentTimer = setTimeout(() => {
        this._snapshotSent.next(false);
        this.snapshotSentTimer = null;
      }, 2000);

      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          realtimeInput: {
            text: '[System: The admin just sent you a single screenshot of their current screen. This is a one-time still image, not a live video feed. Use it to answer their question, and ask for another screenshot if you need an updated view.]'
          }
        }));
      }
    } catch (err: any) {
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        this._error.next(err?.message || 'Failed to capture screenshot');
      }
    } finally {
      stream?.getTracks().forEach(t => t.stop());
      this._sendingSnapshot.next(false);
    }
  }

  setMuted(muted: boolean): void {
    this._micMuted.next(muted);
    // Also disable the track itself so the browser tab-mute indicator
    // reflects state (belt-and-braces; the onaudioprocess guard is what
    // actually stops upload).
    this.micStream?.getAudioTracks().forEach(t => t.enabled = !muted);
  }

  disableVideo(): void {
    if (!this._videoEnabled.value) return;

    this.clearVideoCountdown();
    if (this.screenInterval) {
      clearInterval(this.screenInterval);
      this.screenInterval = null;
    }
    this.screenStream?.getTracks().forEach(t => t.stop());
    this.screenStream = null;
    this.screenVideo = null;
    this.screenCanvas = null;
    this.hashCanvas = null;
    this.lastFrameHash = null;
    this._videoEnabled.next(false);

    // Tell the model it can no longer see the screen. Without this nudge
    // the model will keep referring to whatever was on the last frame still
    // in its sliding window — confusing for the user.
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        realtimeInput: {
          text: '[System: Screen sharing has stopped. You can no longer see the user\'s screen. Ask them to share again if needed.]'
        }
      }));
    }
  }

  private startVideoCountdown(): void {
    this.clearVideoCountdown();
    const totalSec = Math.floor(this.VIDEO_AUTO_DISABLE_MS / 1000);
    this._videoSecondsLeft.next(totalSec);
    this.videoCountdownInterval = setInterval(() => {
      const next = this._videoSecondsLeft.value - 1;
      if (next <= 0) {
        this._videoSecondsLeft.next(0);
        this.disableVideo();
      } else {
        this._videoSecondsLeft.next(next);
      }
    }, 1000);
  }

  private clearVideoCountdown(): void {
    if (this.videoCountdownInterval) {
      clearInterval(this.videoCountdownInterval);
      this.videoCountdownInterval = null;
    }
    if (this.videoTimeout) {
      clearTimeout(this.videoTimeout);
      this.videoTimeout = null;
    }
    this._videoSecondsLeft.next(0);
  }

  @GET('v2/admin/schools/:schoolId/gemini/token')
  private getGeminiToken(queryParams?: any) {
    return undefined as any;
  }

  @POST('v2/admin/schools/:schoolId/gemini/sessions')
  private storeGeminiSession(body: any) {
    return undefined as any;
  }

  private fetchAccessToken(): Observable<string> {
    return this.getGeminiToken().pipe(map((res: any) => res?.access_token));
  }

  private openWebSocket(accessToken: string, kbRouter: string): void {
    const { geminiRegion } = environment;
    const url = `wss://${geminiRegion}-aiplatform.googleapis.com/ws/google.cloud.aiplatform.v1beta1.LlmBidiService/BidiGenerateContent?access_token=${accessToken}`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log('[GeminiLive] WebSocket connected, sending setup...');
      this.sendSetup(kbRouter);
      setTimeout(() => {
        if (this._sessionState.value === 'connecting') {
          this._error.next('Connection timed out. Check your API key and project ID.');
          this.reset();
        }
      }, 10000);
    };
    this.ws.onmessage = (event) => this.handleMessage(event).catch(console.error);
    this.ws.onerror = () => {
      this._error.next('Connection failed. Check your API key and project ID.');
      this.reset();
    };
    this.ws.onclose = (event) => {
      console.log('[GeminiLive] WebSocket closed — code:', event.code, 'reason:', event.reason);
      if (this._sessionState.value === 'active') {
        this.endSession();
      }
    };
  }

  private sendSetup(kbRouter: string): void {
    const { geminiProjectId, geminiRegion } = environment;
    const model = `projects/${geminiProjectId}/locations/${geminiRegion}/publishers/google/models/gemini-live-2.5-flash-native-audio`;

    const setupMsg = {
      setup: {
        model,
        tools: [{ functionDeclarations: this.buildKbFunctionDeclarations() }],
        generationConfig: {
          responseModalities: ['AUDIO'],
          mediaResolution: 'MEDIA_RESOLUTION_LOW',
          // Must stay 0. gemini-live-2.5-flash-native-audio has no thinking
          // variant, and Vertex picks the serving endpoint from this config —
          // any non-zero budget closes the socket at setup with 1007
          // "Failed to select an endpoint for the request". The playbook-vs-
          // procedure choice this was raised for is handled by an explicit
          // routing rule in the system prompt instead.
          thinkingConfig: { thinkingBudget: 0 },
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName: 'Aoede' }
            }
          }
        },
        outputAudioTranscription: {},
        inputAudioTranscription: {},
        systemInstruction: {
          parts: [{ text: this.buildSystemPrompt(kbRouter) }]
        },
        contextWindowCompression: {
          triggerTokens: this.CONTEXT_TRIGGER_TOKENS,
          slidingWindow: { targetTokens: this.CONTEXT_TARGET_TOKENS }
        }
      }
    };

    this.ws!.send(JSON.stringify(setupMsg));
  }

  private sendGreetingTrigger(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      realtimeInput: { text: 'hello' }
    }));
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    let text: string;
    if (event.data instanceof Blob) {
      text = await event.data.text();
    } else {
      text = event.data;
    }

    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      console.log('[GeminiLive] non-JSON message:', text);
      return;
    }

    console.log('[GeminiLive] message:', JSON.stringify(data).slice(0, 300));

    if (data.error) {
      const msg = data.error?.message || JSON.stringify(data.error);
      console.error('[GeminiLive] server error:', msg);
      this._error.next(msg);
      this.reset();
      return;
    }

    if (data.setupComplete) {
      this._sessionState.next('active');
      this.startMicCapture();
      // Screen capture is no longer started here — it's opt-in via
      // enableVideo() (Plan 04). Session runs audio-only by default.
      this.startTimer();
      setTimeout(() => this.sendGreetingTrigger(), 500);
      return;
    }

    if (data.toolCall) {
      await this.handleToolCall(data.toolCall);
      return;
    }

    if (data.serverContent) {
      // Audio chunks
      const parts = data.serverContent?.modelTurn?.parts || [];
      for (const part of parts) {
        if (part.inlineData?.mimeType?.startsWith('audio/pcm')) {
          this.isModelSpeaking = true;
          this.playAudioChunk(part.inlineData.data);
        }
        if (part.text) {
          this.currentAssistantText += part.text;
        }
      }

      if (data.serverContent.interrupted) {
        this.isModelSpeaking = false;
      }

      // Output transcription (what Gemini said)
      if (data.serverContent.outputTranscription?.text) {
        this.currentAssistantText += data.serverContent.outputTranscription.text;
      }

      // Input transcription (what admin said) — buffer chunks, flush on turn boundary
      if (data.serverContent.inputTranscription?.text) {
        this.currentUserText += data.serverContent.inputTranscription.text;
      }

      if (data.serverContent.turnComplete) {
        this.isModelSpeaking = false;
        let changed = false;
        if (this.currentUserText.trim()) {
          this.transcript.push({ role: 'user', text: this.currentUserText.trim() });
          this.currentUserText = '';
          changed = true;
        }
        if (this.currentAssistantText) {
          this.transcript.push({ role: 'assistant', text: this.currentAssistantText });
          this.currentAssistantText = '';
          changed = true;
        }
        if (changed) this._transcript.next([...this.transcript]);
      }
    }
  }

  // --- Knowledge base tools (BK-1381) ---

  /**
   * The router the model already holds tells it to call these before
   * explaining anything, so the descriptions here only need to pin down the
   * argument shapes.
   */
  private buildKbFunctionDeclarations(): any[] {
    return [
      {
        name: 'get_procedure',
        description: 'Fetch the full step-by-step body of a knowledge base procedure. Call this before explaining any how-to. The id comes from the Procedure index in your operating contract.',
        parameters: {
          type: 'OBJECT',
          properties: {
            id: {
              type: 'STRING',
              description: 'Procedure id from the Procedure index, e.g. "split-grade" or "update-grades-and-classes".'
            },
            lang: {
              type: 'STRING',
              enum: ['en', 'ar'],
              description: 'Language the admin is speaking.'
            }
          },
          required: ['id', 'lang']
        }
      },
      {
        name: 'get_playbook',
        description: 'Fetch a multi-stage playbook by id. The id comes from your operating contract.',
        parameters: {
          type: 'OBJECT',
          properties: {
            id: { type: 'STRING', description: 'Playbook id from the operating contract.' }
          },
          required: ['id']
        }
      }
    ];
  }

  private async handleToolCall(toolCall: any): Promise<void> {
    const calls: any[] = toolCall?.functionCalls || [];
    if (!calls.length) return;

    const functionResponses = await Promise.all(
      calls.map(async (call) => ({
        id: call.id,
        name: call.name,
        response: { result: await this.runKbTool(call.name, call.args || {}) }
      }))
    );

    // The session may have ended while the fetch was in flight.
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ toolResponse: { functionResponses } }));
  }

  private runKbTool(name: string, args: any): Promise<KbToolResult> {
    switch (name) {
      case 'get_procedure':
        return this.kbStore.getProcedure(args.id, args.lang);
      case 'get_playbook':
        return this.kbStore.getPlaybook(args.id);
      default:
        console.warn('[GeminiLive] unknown tool call:', name);
        return Promise.resolve({
          status: 'not_found',
          message: `There is no tool named "${name}". Tell the admin you cannot look that up and offer to pass it to the support team. Do not invent or guess the steps.`
        });
    }
  }

  // --- Audio capture ---

  private startMicCapture(): void {
    this.audioContext = new AudioContext({ sampleRate: 16000 });
    const source = this.audioContext.createMediaStreamSource(this.micStream!);
    this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

    this.scriptProcessor.onaudioprocess = (e) => {
      if (this._sessionState.value !== 'active') return;
      if (this._micMuted.value) return;
      const float32 = e.inputBuffer.getChannelData(0);
      const pcm16 = this.float32ToInt16(float32);
      const b64 = this.arrayBufferToBase64(pcm16.buffer as ArrayBuffer);
      this.sendAudioChunk(b64);
    };

    source.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.audioContext.destination);
  }

  private sendAudioChunk(base64: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      realtimeInput: {
        audio: { data: base64, mimeType: 'audio/pcm;rate=16000' }
      }
    }));
  }

  private float32ToInt16(buffer: Float32Array): Int16Array {
    const result = new Int16Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) {
      const s = Math.max(-1, Math.min(1, buffer[i]));
      result[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return result;
  }

  // --- Screen capture ---

  private startScreenCapture(): void {
    this.screenVideo = document.createElement('video');
    this.screenVideo.srcObject = this.screenStream!;
    this.screenVideo.play();

    this.screenCanvas = document.createElement('canvas');
    this.screenCanvas.width = 768;
    this.screenCanvas.height = 432;

    this.screenInterval = setInterval(() => {
      if (this._sessionState.value !== 'active') return;
      // Skip capture while the model is speaking back — the user is
      // listening, not interacting. Frames captured during TTS re-bill in
      // the sliding window for near-zero informational value (Plan 05).
      if (this.isModelSpeaking) return;
      if (!this.screenVideo || !this.screenCanvas) return;
      const ctx = this.screenCanvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(this.screenVideo, 0, 0, 768, 432);
      this.applyPrivacyRedactions(ctx, 768, 432);

      // Skip the send if the frame is visually identical to the previous one
      // we sent. The model already has this scene in its sliding-window
      // context — re-sending burns tokens (billed at ~6× due to per-turn
      // re-processing) for zero information gain.
      const hash = this.computeFrameHash(this.screenCanvas);
      if (
        this.lastFrameHash &&
        this.hammingDistance(hash, this.lastFrameHash) < this.DIFF_THRESHOLD
      ) {
        return;
      }
      this.lastFrameHash = hash;

      const dataUrl = this.screenCanvas.toDataURL('image/jpeg', 0.7);
      if (this.debugDumpNextFrame) {
        this.debugDumpNextFrame = false;
        (window as any).__lastGeminiFrame = dataUrl;
        this.showFrameDumpOverlay(dataUrl);
      }
      // Don't send frames while the debug overlay is open — it would show a
      // black screen to Gemini and then when dismissed Gemini would see a
      // momentary unredacted frame before the next tick.
      if (this.debugOverlayVisible) return;
      const base64 = dataUrl.split(',')[1];
      this.sendScreenFrame(base64);
    }, 2000);

    this.screenStream!.getVideoTracks()[0].onended = () => {
      if (this._sessionState.value === 'active') {
        this.endSession();
      }
    };
  }

  private applyPrivacyRedactions(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number): void {
    const elements = document.querySelectorAll<HTMLElement>('[data-pii="redact"]');
    if (!elements.length) {
      console.warn('[GeminiLive] applyPrivacyRedactions: no [data-pii="redact"] elements found — PII may be exposed!');
      return;
    }

    // Derive scale from the actual captured video frame dimensions rather than
    // window.innerWidth/innerHeight. Opening DevTools changes those values while
    // the video frame lags behind, causing the redaction boxes to land in the
    // wrong position. videoWidth/videoHeight always match what was just drawn
    // to the canvas via drawImage. Divide by devicePixelRatio because
    // getDisplayMedia captures at physical pixels on HiDPI displays while
    // getBoundingClientRect() returns CSS pixels.
    const dpr = window.devicePixelRatio || 1;
    const srcW = this.screenVideo?.videoWidth
      ? this.screenVideo.videoWidth / dpr
      : window.innerWidth;
    const srcH = this.screenVideo?.videoHeight
      ? this.screenVideo.videoHeight / dpr
      : window.innerHeight;

    const scaleX = canvasWidth / srcW;
    const scaleY = canvasHeight / srcH;

    ctx.fillStyle = '#1a1a2e';
    ctx.font = `bold ${Math.round(13 * scaleX)}px monospace`;
    ctx.textBaseline = 'middle';

    elements.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const x = rect.left * scaleX;
      const y = rect.top * scaleY;
      const w = rect.width * scaleX;
      const h = rect.height * scaleY;

      ctx.fillRect(x, y, w, h);

      ctx.fillStyle = '#6b7280';
      ctx.fillText('● ● ● ● ● ● ●', x + 6 * scaleX, y + h / 2);
      ctx.fillStyle = '#1a1a2e';
    });
  }

  // Downsample to 16x16 grayscale and threshold each cell against the mean to
  // produce a 256-bit perceptual hash. Cheap (~256 byte reads per call) and
  // stable under JPEG noise.
  private computeFrameHash(source: HTMLCanvasElement): Uint8Array {
    if (!this.hashCanvas) {
      this.hashCanvas = document.createElement('canvas');
      this.hashCanvas.width = 16;
      this.hashCanvas.height = 16;
    }
    const hctx = this.hashCanvas.getContext('2d');
    const sig = new Uint8Array(32);
    if (!hctx) return sig;

    hctx.drawImage(source, 0, 0, 16, 16);
    const { data } = hctx.getImageData(0, 0, 16, 16);

    const gray = new Uint8Array(256);
    let sum = 0;
    for (let i = 0; i < 256; i++) {
      const r = data[i * 4];
      const g = data[i * 4 + 1];
      const b = data[i * 4 + 2];
      const y = (r * 299 + g * 587 + b * 114) / 1000;
      gray[i] = y;
      sum += y;
    }
    const mean = sum / 256;
    for (let i = 0; i < 256; i++) {
      if (gray[i] > mean) sig[i >> 3] |= 1 << (i & 7);
    }
    return sig;
  }

  private hammingDistance(a: Uint8Array, b: Uint8Array): number {
    let d = 0;
    for (let i = 0; i < a.length; i++) {
      let x = a[i] ^ b[i];
      // Brian Kernighan popcount
      while (x) { x &= x - 1; d++; }
    }
    return d;
  }

  private sendScreenFrame(base64: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({
      realtimeInput: {
        video: { data: base64, mimeType: 'image/jpeg' }
      }
    }));
  }

  // --- Audio playback ---

  private playAudioChunk(base64: string): void {
    if (!this.audioContext) return;
    const raw = atob(base64);
    const int16 = new Int16Array(raw.length / 2);
    for (let i = 0; i < int16.length; i++) {
      int16[i] = (raw.charCodeAt(i * 2)) | (raw.charCodeAt(i * 2 + 1) << 8);
    }
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768;
    }

    const sampleRate = 24000;
    const buffer = this.audioContext.createBuffer(1, float32.length, sampleRate);
    buffer.copyToChannel(float32, 0);

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioContext.destination);

    const now = this.audioContext.currentTime;
    if (this.nextAudioTime < now) this.nextAudioTime = now;
    source.start(this.nextAudioTime);
    this.nextAudioTime += buffer.duration;
  }

  // --- Session end & summary ---

  endSession(): void {
    if (this._sessionState.value === 'idle' || this._sessionState.value === 'ending') return;
    if (this._sessionState.value === 'connecting') { this.reset(); return; }
    this._sessionState.next('ending');
    this.stopTimer();
    this.stopMediaTracks();

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.close();
    }

    this.generateAiSummary().then(result => {
      this._summary.next(result);
      this.postSession(result);
      this._sessionState.next('summary');
    }).catch((err) => {
      console.error('[GeminiLive] generateAiSummary failed:', err);
      const elapsed = this._elapsed.value;
      const [m, s] = elapsed.split(':').map(Number);
      const fallback: SessionSummary = {
        title:           'Support Session',
        summary:         `Session completed — Duration: ${elapsed}`,
        tags:            [],
        priority:        'low',
        language:        'en',
        durationSeconds: m * 60 + s,
        turnCount:       this.transcript.length,
      };
      this._summary.next(fallback);
      this.postSession(fallback);
      this._sessionState.next('summary');
    });
  }

  private async generateAiSummary(): Promise<SessionSummary> {
    const { geminiProjectId, geminiRegion } = environment;
    const token = await this.fetchAccessToken().toPromise();

    const elapsed = this._elapsed.value;
    const [m, s] = elapsed.split(':').map(Number);
    const durationSeconds = m * 60 + s;

    // Guard on user turns, not transcript length. The greeting trigger
    // produces an assistant-only transcript on silent sessions, which the
    // summary model will happily confabulate a full conversation around
    // (Plan 13 §4.5).
    const userTurns = this.transcript.filter(e => e.role === 'user').length;
    if (userTurns === 0) {
      return {
        title: 'Support Session',
        summary: `Session ended before any question was asked — Duration: ${elapsed}`,
        tags: [], priority: 'low', language: 'en',
        durationSeconds, turnCount: this.transcript.length,
      };
    }

    const transcriptText = this.transcript
      .map(e => `[${e.role === 'user' ? 'Admin' : 'Assistant'}]: ${e.text}`)
      .join('\n');

    const prompt = `You are analyzing a support conversation between a school admin and BlueRide's AI assistant.

Return ONLY valid JSON matching this exact structure (no markdown, no explanation):
{
  "title": "<short ticket title, max 10 words>",
  "summary": "<2-3 sentence summary of what was discussed and resolved>",
  "tags": ["<tag1>", "<tag2>"],
  "priority": "<low|medium|high>",
  "language": "<ar|en|mixed>"
}

Tag vocabulary (use only from this list, pick all that apply):
buses, students, attendance, grades, messages, appointments, reports, login, permissions, scanner, cafeteria, school-settings, payments, notifications, onboarding, bug, other

Priority rules:
- high: admin couldn't complete a critical task, reported a bug, or expressed frustration
- medium: admin needed guidance but completed their task
- low: general how-to question, quick answer

Rules:
- If the transcript contains no real user question, return title "Empty Session", empty summary, empty tags array.
- Do not invent details. Do not infer the admin's name. Do not reference features the admin did not ask about.
- Summarize only content explicitly present in the transcript below.

Conversation transcript:
${transcriptText}`;

    const res = await fetch(
      `https://${geminiRegion}-aiplatform.googleapis.com/v1beta1/projects/${geminiProjectId}/locations/${geminiRegion}/publishers/google/models/gemini-2.5-flash-lite:generateContent`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ role: 'user', parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.2, maxOutputTokens: 512 }
        })
      }
    );

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`[GeminiLive] summary API ${res.status}: ${errText}`);
    }

    const data = await res.json();
    const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? '{}';

    console.log('[GeminiLive] summary raw response:', raw);

    // Strip markdown code fences if model wraps the JSON (common even when instructed not to)
    const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').trim();

    let parsed: any = {};
    try { parsed = JSON.parse(cleaned); } catch (e) {
      console.error('[GeminiLive] JSON parse failed. Raw was:', raw);
    }

    return {
      title:           parsed.title    ?? 'Support Session',
      summary:         parsed.summary  ?? '',
      tags:            Array.isArray(parsed.tags) ? parsed.tags : [],
      priority:        parsed.priority ?? 'low',
      language:        parsed.language ?? 'en',
      durationSeconds,
      turnCount:       this.transcript.length,
    };
  }

  private postSession(result: SessionSummary): void {
    const userId   = localStorage.getItem('userId');
    const userRole = localStorage.getItem('loginAs');
    this.storeGeminiSession({
      userId,
      userRole,
      title:           result.title,
      summary:         result.summary,
      tags:            result.tags,
      priority:        result.priority,
      language:        result.language,
      durationSeconds: result.durationSeconds,
      turnCount:       result.turnCount,
    }).pipe(catchError(() => EMPTY)).subscribe();
  }

  dismissSummary(): void {
    this._sessionState.next('idle');
    this._summary.next(null);
    this.transcript = [];
    this._elapsed.next('00:00');
  }

  // --- Timer ---

  private startTimer(): void {
    this.sessionStartTime = Date.now();
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.sessionStartTime!) / 1000);
      const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const s = (elapsed % 60).toString().padStart(2, '0');
      this._elapsed.next(`${m}:${s}`);
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // --- Cleanup ---

  private stopMediaTracks(): void {
    this.micStream?.getTracks().forEach(t => t.stop());
    this.screenStream?.getTracks().forEach(t => t.stop());
    this.micStream = null;
    this.screenStream = null;

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.screenInterval) {
      clearInterval(this.screenInterval);
      this.screenInterval = null;
    }
    this.screenVideo = null;
    this.screenCanvas = null;
    this.hashCanvas = null;
    this.lastFrameHash = null;
    this.nextAudioTime = 0;
  }

  private reset(): void {
    this.stopMediaTracks();
    this.stopTimer();
    this.clearVideoCountdown();
    this.ws = null;
    this.isModelSpeaking = false;
    this._videoEnabled.next(false);
    this._micMuted.next(false);
    this._sessionState.next('idle');
  }

  // --- Helpers ---

  /**
   * Session identity plus the live operating contract (BK-1381).
   *
   * Everything that used to be hand-written here — tone, language handling,
   * screenshot etiquette, the privacy rules, the never-invent-a-button rule —
   * now arrives in the `channel=voice` router and is maintained by the
   * knowledge base team. Restating any of it locally risks contradicting the
   * live copy, so only the per-session facts the router cannot know are kept.
   */
  private buildSystemPrompt(kbRouter: string): string {
    const name = localStorage.getItem('userName') || 'there';
    const school = localStorage.getItem('schoolName') || 'your school';
    const role = localStorage.getItem('loginAs') || 'admin';
    const city = localStorage.getItem('city') || '';

    return `The admin you are speaking with is:
- Name: ${name}
- School: ${school}
- Role: ${role}${city ? '\n- City: ' + city : ''}

Greet them by first name.

Tool routing — decide this before you answer, without thinking out loud:
- One concrete task ("how do I split a grade") -> call get_procedure.
- A goal that spans several tasks, a setup or onboarding, "where do I start",
  or anything the router lists as a journey -> call get_playbook first, then
  get_procedure for the step you are on.
- If both look plausible, call get_playbook. Never answer a multi-step goal
  from a single procedure.

---

${kbRouter}`;
  }

  private showFrameDumpOverlay(dataUrl: string): void {
    document.getElementById('gemini-frame-dump')?.remove();

    const overlay = document.createElement('div');
    overlay.id = 'gemini-frame-dump';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.9);z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;';

    const header = document.createElement('div');
    header.style.cssText = 'color:white;font:14px monospace;margin-bottom:10px;text-align:center;';
    header.textContent = 'Gemini uploaded frame — what the model actually sees. Click anywhere or press Esc to close.';

    const img = document.createElement('img');
    img.src = dataUrl;
    img.style.cssText = 'max-width:100%;max-height:calc(100vh - 80px);border:2px solid #fff;box-shadow:0 0 20px rgba(0,0,0,0.8);';

    overlay.appendChild(header);
    overlay.appendChild(img);
    document.body.appendChild(overlay);
    this.debugOverlayVisible = true;

    const close = () => {
      overlay.remove();
      this.debugOverlayVisible = false;
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    console.log('[GeminiLive] frame dumped — shown as overlay. Also saved to window.__lastGeminiFrame');
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
